<template>
    <section class="flex flex-col gap-4">
        <div class="flex items-center justify-between gap-3 border-b border-border pb-3">
            <h2 class="display-small-serif text-ink">{{ t("teamPage.members") }}</h2>
            <BaseChip size="2xs">{{ t("teamPage.memberCount", { count: report.members.length }) }}</BaseChip>
        </div>
        <div class="overflow-x-auto rounded-lgr border border-border bg-surface-raised">
            <table class="team-members" :aria-label="t('teamPerspective.members')">
                <thead>
                    <tr>
                        <th scope="col">{{ t("teamPage.name") }}</th>
                        <th scope="col">{{ t("teamPage.perspective") }}</th>
                        <th scope="col" class="hidden md:table-cell">{{ t("teamPage.joined") }}</th>
                        <th scope="col">{{ t("teamPage.profile") }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="member in report.members"
                        :key="member.id"
                        class="group relative"
                        :class="{ 'bg-surface/60': member.id === report.viewer_id, 'hover:bg-surface focus-within:bg-surface': member.member_url || member.profile_url }">
                        <td>
                            <div class="flex items-center gap-3">
                                <PerspectiveAvatar :name="member.name" :letters="member.result?.letters" size="sm" aria-hidden="true" />
                                <div class="min-w-0">
                                    <div class="flex flex-wrap items-center gap-2">
                                        <BaseLink
                                            v-if="member.member_url || member.profile_url"
                                            :href="member.member_url || member.profile_url"
                                            class="team-member-link body-serif font-semibold text-ink"
                                            :aria-label="t('teamPage.viewMember', { name: member.name })"
                                            >{{ member.name }}</BaseLink
                                        >
                                        <span v-else class="body-serif font-semibold text-ink">{{ member.name }}</span>
                                        <BaseChip v-if="member.id === report.viewer_id" size="2xs">{{ t("teamPage.you") }}</BaseChip>
                                    </div>
                                    <p v-if="member.job_title" class="body-small truncate text-ink-muted">{{ member.job_title }}</p>
                                    <p v-if="member.joined_at" class="details text-ink-muted md:hidden">
                                        {{ t("teamPage.joinedOn", { date: formatDate(member.joined_at, "date") }) }}
                                    </p>
                                </div>
                            </div>
                        </td>
                        <td class="scaffold-body-small text-ink">
                            <span class="flex items-center gap-2">
                                <span
                                    v-if="getPerspectiveSwatch(member.result?.letters)"
                                    class="size-3 shrink-0 rounded-full"
                                    :style="{ backgroundColor: getPerspectiveSwatch(member.result?.letters)?.fill }"
                                    aria-hidden="true" />
                                {{ member.result?.archetype || t("teamPage.noSharedResult") }}
                            </span>
                        </td>
                        <td class="hidden md:table-cell">
                            <BaseDate v-if="member.joined_at" :value="member.joined_at" format="date" class="scaffold-body-small text-ink-muted" /><span
                                v-else
                                aria-hidden="true"
                                >{{ t("teamPage.notAvailable") }}</span
                            >
                        </td>
                        <td>
                            <i
                                v-if="member.member_url || member.profile_url"
                                class="bi bi-chevron-right pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-ink-muted opacity-0 group-hover:opacity-100 group-focus-within:opacity-100"
                                aria-hidden="true" />
                            <span class="sr-only">{{ member.profile_url ? t("teamPage.sharedProfile", { name: member.name }) : t("teamPage.noProfile") }}</span>
                            <i v-if="member.profile_url" class="bi bi-check-circle-fill text-ink-muted" aria-hidden="true" /><span v-else aria-hidden="true">{{
                                t("teamPage.notAvailable")
                            }}</span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <p class="details text-ink-muted">{{ t("teamPerspective.sharing") }}</p>
    </section>
</template>
<script setup lang="ts">
import type { TeamPerspectiveReport } from "~vue/api/generated/team-perspective";
import BaseChip from "~vue/components/BaseChip.vue";
import BaseDate from "~vue/components/BaseDate.vue";
import PerspectiveAvatar from "~vue/components/PerspectiveAvatar.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import { formatDate } from "~vue/utils/formatDate";
import { getPerspectiveSwatch } from "~vue/utils/perspectivePalette";
import { useI18n } from "vue-i18n";
defineProps<{ report: TeamPerspectiveReport }>();
const { t } = useI18n();
</script>
<style scoped>
@reference "../../../css/tailwind.css";
@layer components {
    .team-members {
        @apply w-full min-w-150 table-fixed border-collapse text-left md:min-w-0;
    }
    .team-members th {
        @apply scaffold-body-small font-semibold text-ink-muted px-4 py-3;
    }
    .team-members td {
        @apply border-t border-border px-4 py-4;
    }
    .team-member-link::after {
        content: "";
        @apply absolute inset-0 z-10;
    }
    .team-members th:nth-child(2) {
        @apply w-1/3 md:w-1/4;
    }
    .team-members th:first-child {
        @apply w-1/2 md:w-[36%];
    }
}
</style>
